import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";

export interface UseFileNavigationOptions {
  hasMore?: boolean;
  loadMore?: () => Promise<void>;
  isLoadingMore?: boolean;
}

/**
 * Manages file navigation (previous/next) and keyboard shortcuts
 */
export const useFileNavigation = (
  file: FormattedFile | null,
  files: FormattedFile[],
  onFileOpen: (file: FormattedFile) => void,
  options?: UseFileNavigationOptions
) => {
  const { hasMore = false, loadMore, isLoadingMore = false } = options ?? {};
  const [pendingNav, setPendingNav] = useState<"next" | null>(null);
  const filesLengthRef = useRef(files.length);

  const currentFileIndex = useMemo(() => {
    if (!file) return -1;
    return files.findIndex((f) => f.id === file.id);
  }, [file, files]);

  const hasPrevious = currentFileIndex > 0;
  const hasNextInLoaded = currentFileIndex >= 0 && currentFileIndex < files.length - 1;
  const hasNextAtBoundary =
    currentFileIndex >= 0 && currentFileIndex === files.length - 1 && hasMore;
  const hasNext = hasNextInLoaded || hasNextAtBoundary;

  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      onFileOpen(files[currentFileIndex - 1]);
    }
  }, [hasPrevious, currentFileIndex, files, onFileOpen]);

  const handleNext = useCallback(async () => {
    if (hasNextInLoaded) {
      onFileOpen(files[currentFileIndex + 1]);
      return;
    }

    if (hasNextAtBoundary && loadMore && !isLoadingMore) {
      setPendingNav("next");
      filesLengthRef.current = files.length;
      try {
        await loadMore();
      } catch {
        setPendingNav(null);
      }
    }
  }, [hasNextInLoaded, hasNextAtBoundary, loadMore, isLoadingMore, currentFileIndex, files, onFileOpen]);

  useEffect(() => {
    if (pendingNav !== "next" || !file || currentFileIndex < 0) {
      return;
    }

    if (currentFileIndex < files.length - 1) {
      onFileOpen(files[currentFileIndex + 1]);
      setPendingNav(null);
      return;
    }

    if (!isLoadingMore && files.length === filesLengthRef.current) {
      setPendingNav(null);
    }
  }, [pendingNav, file, currentFileIndex, files, isLoadingMore, onFileOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        void handleNext();
      }
    },
    [handlePrevious, handleNext]
  );

  return {
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,
    handleKeyDown,
  };
};

export const useFileNavigationKeyboard = (
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  handlePrevious: () => void,
  handleNext: () => void | Promise<void>
) => {
  useEffect(() => {
    if (!active) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
        return;
      }

      const container = containerRef.current;
      if (!container?.contains(document.activeElement)) {
        return;
      }

      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.hasAttribute("contenteditable"));

      if (isInputFocused) {
        return;
      }

      e.preventDefault();
      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else {
        void handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [active, containerRef, handlePrevious, handleNext]);
};

export const useFocusedNavigationContainer = (active: boolean, focusKey?: string | number) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const focusContainer = useCallback(() => {
    requestAnimationFrame(() => {
      containerRef.current?.focus({ preventScroll: true });
    });
  }, []);

  useLayoutEffect(() => {
    if (active) {
      focusContainer();
    }
  }, [active, focusKey, focusContainer]);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (node && active) {
        focusContainer();
      }
    },
    [active, focusContainer]
  );

  return {
    containerRef,
    focusContainer,
    focusContainerProps: {
      ref: setContainerRef,
      tabIndex: -1 as const,
      onPointerDown: () => containerRef.current?.focus({ preventScroll: true }),
    },
  };
};
